/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DOMParser } from '@tiptap/pm/model';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { ModelEditorFactory } from '@leverege/ui-plugin';
import { Pane, Popup } from '@leverege/ui-elements';
import i18n from '@leverege/i18n';
import { Attributes } from "../../../../attribute/index.js";
import S from './WidgetNode.css';
import CustomTextAttributeRenderers from "./valueRenderers/CustomTextAttributeRenderers.js";
import { GenericCustomTextAttributeRenderer } from "./valueRenderers/GenericCustomTextAttributeRenderer.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = i18n.ns('ui-attributes.detail.customText.textEditor.extensions.WidgetNode');
function EmptyValueSource(props) {
  const {
    value,
    deleteNode,
    copyNode,
    onChange,
    editor: Editor,
    ...rest
  } = props;
  const attribute = Attributes.getAttribute(value?.attrName, value?.objectType);
  const onDeleteNode = useCallback(() => {
    copyNode();
    deleteNode();
  }, [deleteNode, copyNode]);
  if (!attribute) {
    return null;
  }
  const canEdit = Editor?.canEdit == null || Editor.canEdit(value);
  return /*#__PURE__*/_jsxs(Popup, {
    variant: "valueSourcePill",
    toggleVariant: "valueSourcePill",
    icon: tIcon('edit', 'fak fa-lv-chevron-down-small'),
    title: attribute.displayName,
    children: [/*#__PURE__*/_jsx(Popup.Item, {
      icon: tIcon('cut', 'fa fa-fw fa-cut'),
      onClick: onDeleteNode,
      children: t('cut')
    }), /*#__PURE__*/_jsx(Popup.Item, {
      icon: tIcon('copy', 'fak fa-lv-copy'),
      onClick: copyNode,
      children: t('copy')
    }), canEdit && Editor && [/*#__PURE__*/_jsx(Popup.Divider, {}, "div"), /*#__PURE__*/_jsx(Popup.Menu, {
      title: "Edit",
      children: /*#__PURE__*/_jsx(Pane, {
        variant: "templateTagEditor",
        children: /*#__PURE__*/_jsx(Editor, {
          ...rest,
          value: value,
          onChange: onChange
        })
      })
    }, "ed")]]
  });
}
function WidgetNodeComponent(props) {
  const {
    node,
    updateAttributes,
    deleteNode,
    editor
  } = props;
  const attrValue = node.attrs?.['data-value'];
  const value = useMemo(() => {
    try {
      return JSON.parse(attrValue);
    } catch (err) {
      return null;
    }
  }, [attrValue]);
  const onChange = useCallback(({
    value: nValue,
    data
  }) => {
    let val = nValue;
    try {
      val = JSON.stringify(val);
    } catch (err) {}
    updateAttributes({
      'data-value': val
    });
  }, [updateAttributes]);
  const attribute = Attributes.getAttribute(value?.attrName, value?.objectType);
  const copyNode = useCallback(() => {
    navigator.clipboard.writeText(`data:leverege/widget,${JSON.stringify(value)}`);
  }, [value]);
  let content;
  if (!editor.options.editable && attribute) {
    const {
      object
    } = editor?.options?.editorProps || {};
    const renderer = CustomTextAttributeRenderers.getRendererFor(value?.attrName, object || value.objectType);
    const v = attribute.get(object?.data);
    if (!renderer) {
      if (!value.formatter) {
        content = null;
      } else {
        content = /*#__PURE__*/_jsx(GenericCustomTextAttributeRenderer, {
          ...editor?.options?.editorProps,
          value: v,
          model: value
        });
      }
    } else {
      const {
        Component
      } = renderer;
      content = /*#__PURE__*/_jsx(Component, {
        ...editor?.options?.editorProps,
        value: v,
        model: value
      });
    }
  } else {
    const Editor = ModelEditorFactory.get(value);
    content = /*#__PURE__*/_jsx(EmptyValueSource, {
      ...editor?.options?.editorProps,
      deleteNode: deleteNode,
      copyNode: copyNode,
      value: value,
      onChange: onChange,
      editor: Editor
    });
  }
  return /*#__PURE__*/_jsx(NodeViewWrapper, {
    as: "span",
    className: classnames(S.widget, 'lev-widget', {
      [S.editing]: editor.options.editable
    }),
    children: content
  });
}
const WidgetNode = Node.create({
  name: 'widgetNode',
  group: 'inline',
  inline: true,
  selectable: true,
  draggable: true,
  atom: true,
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(WidgetNodeComponent);
  },
  parseHTML() {
    return [{
      tag: 'lev-widget'
    }];
  },
  renderHTML({
    HTMLAttributes
  }) {
    return ['lev-widget', mergeAttributes(HTMLAttributes)];
  },
  addAttributes() {
    return {
      'data-value': {
        default: null,
        parseHTML: element => element.getAttribute('data-value')
      }
    };
  },
  addCommands() {
    return {
      addWidget: options => ({
        commands
      }) => {
        let dataValue;
        try {
          dataValue = JSON.stringify(options.value);
        } catch (err) {}
        commands.insertContent({
          type: this.name,
          attrs: {
            ...options,
            'data-value': dataValue
          }
        });
      }
    };
  },
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('widgetPaste'),
      props: {
        clipboardTextParser(str, $context, _, view) {
          const LEVEREGE_WIDGET_REGEX = /^data:leverege\/widget,(.+)/g;
          const matches = LEVEREGE_WIDGET_REGEX.exec(str);
          if (matches && matches[1]) {
            const doc = document.cloneNode(false);
            const dom = doc.createElement('span');
            dom.innerHTML = `<lev-widget data-value=${matches[1]}></lev-widget>`;
            const parser = DOMParser.fromSchema(view.state.schema);
            return parser.parseSlice(dom, {
              preserveWhitespace: true,
              context: $context
            });
          }
          return null;
        }
      }
    })];
  }
});
export default {
  id: 'Rich.Text.Editor.Extension.WidgetNode',
  extension: WidgetNode
};